/**
 * Coach grade definitions ordered by qualification superiority
 * (TBF foundation → Diploma → TBF National → International → FIBA).
 */

export const COACH_GRADES = [
  {
    value: 'TBF National Basic Introduction',
    label: 'TBF National Basic Introduction',
    prefix: 'NBI',
    order: 1,
    icon: 'BookOpen',
    description: 'Entry-level TBF coaching introduction for new coaches.',
  },
  {
    value: 'TBF Mini Basketball',
    label: 'TBF Mini Basketball',
    prefix: 'MIN',
    order: 2,
    icon: 'Baby',
    description: 'Certified to coach TBF mini basketball and youth development programs.',
  },
  {
    value: 'Diploma in Sports',
    label: 'Diploma in Sports',
    prefix: 'DIP',
    order: 3,
    icon: 'GraduationCap',
    description: 'General sports coaching diploma qualification.',
  },
  {
    value: 'TBF National Level 1',
    label: 'TBF National Level 1',
    prefix: 'NL1',
    order: 4,
    icon: 'Award',
    description: 'TBF national coaching certification — Level 1.',
  },
  {
    value: 'TBF National Level 2',
    label: 'TBF National Level 2',
    prefix: 'NL2',
    order: 5,
    icon: 'Medal',
    description: 'TBF national coaching certification — Level 2.',
  },
  {
    value: 'International Diploma in Basketball Coaching',
    label: 'International Diploma in Basketball Coaching',
    prefix: 'IDB',
    order: 6,
    icon: 'Globe2',
    description: 'Internationally recognized diploma in basketball coaching.',
  },
  {
    value: 'FIBA Level 1',
    label: 'FIBA Level 1',
    prefix: 'FL1',
    order: 7,
    icon: 'Globe',
    description: 'FIBA Level 1 coaching certification.',
  },
  {
    value: 'FIBA Level 2',
    label: 'FIBA Level 2',
    prefix: 'FL2',
    order: 8,
    icon: 'Trophy',
    description: 'FIBA Level 2 coaching certification.',
  },
  {
    value: 'FIBA Coaches Instructor',
    label: 'FIBA Coaches Instructor',
    prefix: 'FCI',
    order: 9,
    icon: 'Star',
    description: 'FIBA coaches instructor certification — highest coaching grade.',
  },
];

/** Legacy grades retained for existing records and backward compatibility. */
export const LEGACY_COACH_GRADES = [
  { value: 'Level 1', label: 'Level 1 (Legacy)', prefix: 'L1', order: 101, icon: 'GraduationCap', description: 'Legacy beginner grade.' },
  { value: 'Level 2', label: 'Level 2 (Legacy)', prefix: 'L2', order: 102, icon: 'Award', description: 'Legacy intermediate grade.' },
  { value: 'Level 3', label: 'Level 3 (Legacy)', prefix: 'L3', order: 103, icon: 'Medal', description: 'Legacy advanced grade.' },
  { value: 'National Coach', label: 'National Coach (Legacy)', prefix: 'NAT', order: 104, icon: 'Flag', description: 'Legacy national coach grade.' },
  { value: 'FIBA Certified', label: 'FIBA Certified (Legacy)', prefix: 'FIB', order: 105, icon: 'Globe', description: 'Legacy FIBA certified grade.' },
  { value: 'Youth Coach', label: 'Youth Coach (Legacy)', prefix: 'YTH', order: 106, icon: 'Users', description: 'Legacy youth coach grade.' },
];

export const ALL_COACH_GRADES = [...COACH_GRADES, ...LEGACY_COACH_GRADES];

export const COACH_GRADE_OPTIONS = COACH_GRADES.map(({ value, label }) => ({ value, label }));

export const LEGACY_COACH_GRADE_OPTIONS = LEGACY_COACH_GRADES.map(({ value, label }) => ({
  value,
  label,
}));

export const COACH_GRADE_PREFIX_MAP = Object.fromEntries(
  ALL_COACH_GRADES.map(({ value, prefix }) => [value, prefix])
);

export const COACH_GRADE_CATEGORY_CONFIG = [
  ...COACH_GRADES.map((grade) => ({
    key: grade.value,
    label: grade.label.includes('Coach') ? grade.label : `${grade.label} Coaches`,
    description: grade.description,
    icon: grade.icon,
    order: grade.order,
  })),
  ...LEGACY_COACH_GRADES.map((grade) => ({
    key: grade.value,
    label: `${grade.label.replace(' (Legacy)', '')} Coaches`,
    description: grade.description,
    icon: grade.icon,
    order: grade.order,
  })),
  {
    key: 'Other',
    label: 'Other Coaches',
    description: 'Coaches with uncategorized or custom grade assignments.',
    icon: 'User',
    order: 999,
  },
];

export const getCoachGradeOptions = (currentGrade = '') => {
  const options = [...COACH_GRADE_OPTIONS];
  if (currentGrade && !options.some((option) => option.value === currentGrade)) {
    const legacy = LEGACY_COACH_GRADES.find((grade) => grade.value === currentGrade);
    options.push({
      value: currentGrade,
      label: legacy?.label || `${currentGrade} (Current)`,
    });
  }
  return options;
};

export const getCoachGradeLabel = (grade) => {
  if (!grade) return 'Grade N/A';
  const match = ALL_COACH_GRADES.find((item) => item.value === grade);
  return match?.label?.replace(' (Legacy)', '') || grade;
};
